import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { useModal } from '../../../../components/layout';
import WebSourceForm from '../../../../components/data-sources/web-sources/WebSourceForm';
import { API_ENDPOINTS } from '../../../../config/api.config';
import { WebSource, WebSourceFormData } from '../../../../types';
import { transformFormDataToWebSource } from "../../../../utils/webSourceUtils";
import { apiService } from '../../../../services/apiService';
import DeleteConfirmationModal from '../../../../components/modals/common/DeleteConfirmationModal';
import ContentSkeleton from '../../../../components/loading/ContentSkeleton';

interface ApiResponse {
    webSource: WebSource;
}

const EditWebSource: React.FC = () => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const modalState = useModal();
    const [isLoading, setIsLoading] = useState(true);
    const [formData, setFormData] = useState<WebSourceFormData>({
        name: '',
        description: '',
        url: '',
        crawl_frequency: 'daily',
        sitemap_url: '',
        crawl_depth: 2
    });

    const fetchWebSource = async () => {
        try {
            const apiUrl = API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.GET.replace(':id', id || '');
            const data = await apiService.get<ApiResponse>(apiUrl);

            const formattedData = {
                name: data.webSource.name,
                description: data.webSource.description || '',
                url: data.webSource.configuration.url,
                crawl_frequency: data.webSource.configuration.crawl_frequency || 'daily',
                sitemap_url: data.webSource.configuration.sitemap_url || '',
                crawl_depth: data.webSource.configuration.crawl_depth || 2
            };

            setFormData(formattedData);
        } catch (err) {
            console.error('Error fetching web source:', err);
            modalState.setError(err instanceof Error ? err.message : 'Failed to fetch web source');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchWebSource();
        }
    }, [id]);

    const handleSubmit = async (formData: WebSourceFormData) => {
        modalState.setIsLoading(true);
        modalState.setError(null);

        try {
            const apiData = transformFormDataToWebSource(formData);
            const updateUrl = API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.UPDATE.replace(':id', id || '');
            await apiService.put(updateUrl, apiData);
            modalState.setSuccess('Web source updated successfully');
            navigate('/data-sources/web');
        } catch (err) {
            modalState.setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            modalState.setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        modalState.setIsLoading(true);
        modalState.setError(null);

        try {
            const deleteUrl = API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.DELETE.replace(':id', id || '');
            await apiService.delete(deleteUrl);
            modalState.setSuccess('Web source deleted successfully');
            navigate('/data-sources/web');
        } catch (err) {
            modalState.setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            modalState.setIsLoading(false);
            setShowDeleteModal(false);
        }
    };

    const handleCancel = () => {
        navigate('/data-sources/web');
    };

    if (!user) return null;

    return (
        <div className="page-container">
            <div className="content-header">
                <h5 className="mb-4 fw-semibold">Edit Web Source</h5>
                {isLoading ? (
                    <ContentSkeleton />
                ) : (
                    <>
                        <WebSourceForm
                            initialData={formData}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            onDelete={async () => setShowDeleteModal(true)}
                            error={modalState.error}
                            isLoading={modalState.isLoading}
                            isEdit={true}
                        />
                        <DeleteConfirmationModal
                            isOpen={showDeleteModal}
                            onClose={() => setShowDeleteModal(false)}
                            onConfirm={handleDelete}
                            resourceType="Web Source"
                            resourceName={formData?.name || ''}
                            isLoading={modalState.isLoading}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default EditWebSource;